import React, { useState, MouseEvent, useEffect } from 'react'
import { Dropzone, IFileObject } from 'transfix-ui/components/Dropzone'
import { Box, Grid, Typography } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { AttachmentList } from 'apps/fleet-planner/components/AttachmentList'
import { FP_HOME } from 'apps/fleet-planner/config'
import { useActivationContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { IFileCreate } from 'apps/fleet-planner/types/loads'
import { mapDropzoneFiles, documentUploadCaption } from 'apps/fleet-planner/utils/fileUtils'
import { Button, InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'
import { postMessageToMobileApp } from 'utils/mobileApp'
import { VALID_CONTENT_TYPES } from './constants'
import { useUploadW9FormMutation } from './graphql/UploadW9Form'

export const DocumentsUpload = () => {
  const { trackEvent } = useFMSAnalytics()
  const {
    carrierDetails,
    errors,
    isLoading,
    isSafetyLetterNeeded,
    mobileApp,
    sendDocumentEmail,
    sendPacketEmail,
    setCurrentStep,
    setShowActivationToast,
    submitPackageFiles,
  } = useActivationContext()
  const { isMobile } = useBreakpointContext()
  const [files, setFiles] = useState<IFileCreate[]>([])
  const [fileTypeError, setFileTypeError] = useState<string>('')
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const { fleetPlannerMotorCarrierId, id } = carrierDetails || {}
  const [uploadW9Form] = useUploadW9FormMutation()
  const [W9File] = files || []
  const { file: w9FileBase64, fileFileName: w9FileName } = W9File || {}
  const isCarrierAppNewW9UploadEnabled = useFeatureFlag('carrierAppNewW9Upload')
  const isCarrierAppW9RestrictUploadEnabled = useFeatureFlag('carrierAppW9RestrictUpload')
  const isSubmitSafetyLetterEnabled = useFeatureFlag('fmsSubmitSafetyLetter')
  const { caption, isFilesSizeLimitExceeded } = documentUploadCaption(files) || {}

  const showSafetyLetterPage = isSafetyLetterNeeded && isSubmitSafetyLetterEnabled

  const fileChangeHandler = (docs: IFileObject[]) => {
    if (docs) {
      const pkgFiles = mapDropzoneFiles(docs)
      if (pkgFiles) setFiles([...files, ...pkgFiles])
      setFileTypeError('')
    }
  }

  const fileDeleteHandler = (event: MouseEvent<HTMLButtonElement>) => {
    const { currentTarget } = event || {}
    const { id } = currentTarget || {}
    if (id) {
      setFiles(files?.filter(({ uuid }) => uuid !== id))
    }
  }

  const enrollmentSuccessHandler = () => {
    if (showSafetyLetterPage) {
      setCurrentStep('safetyLetter')
    } else {
      setShowActivationToast(true)
      postMessageToMobileApp(mobileApp, 'DOCUMENT_UPLOAD_COMPLETE')
      window.location.replace(`${__ROOT__}/fms${FP_HOME}`)
    }
  }

  const skipHandler = () => {
    if (showSafetyLetterPage) {
      setCurrentStep('safetyLetter')
    } else {
      trackEvent('Setup Guide', 'SKIPPED_UPLOAD_DOCUMENTS')
      postMessageToMobileApp(mobileApp, 'SKIPPED_DOCUMENT_UPLOAD')
      window.location.replace(`${__ROOT__}/fms${FP_HOME}`)
    }
  }

  const oldSubmitHandler = async () => {
    const response = await submitPackageFiles(id, { packageFiles: files })
    const { packageFiles = [] } = response || {}
    if (packageFiles.length) {
      trackEvent('Setup Guide', 'UPLOAD_DOCUMENTS_SUCCESS')
      sendPacketEmail(id)
      sendDocumentEmail(id)
      enrollmentSuccessHandler()
    } else {
      trackEvent('Setup Guide', 'UPLOAD_DOCUMENTS_FAILURE', { errors })
    }
  }

  const newSubmitHandler = async () => {
    try {
      const { data } = await uploadW9Form({
        variables: {
          input: {
            motorCarrierId: fleetPlannerMotorCarrierId,
            w9FormParams: {
              file: w9FileBase64 as string,
              fileFileName: w9FileName,
            },
          },
        },
      })
      const { uploadW9Form: w9FormSubmission } = data || {}
      const { success, errors = [] } = w9FormSubmission || {}

      if (success) {
        trackEvent('Setup Guide', 'UPLOAD_DOCUMENTS_SUCCESS')
        sendDocumentEmail(id)
        enrollmentSuccessHandler()
      } else {
        trackEvent('Setup Guide', 'UPLOAD_DOCUMENTS_FAILURE', { errors })
        const errorMessageStrings = errors.map(err => err.message)
        setErrorMessages(errorMessageStrings)
      }
    } catch (error) {
      trackEvent('Setup Guide', 'UPLOAD_DOCUMENTS_FAILURE', { errors: error })
      setErrorMessages([
        'Something went wrong, please check your information and try submitting again',
      ])
    }
  }

  const submitHandler = () => {
    if (isCarrierAppNewW9UploadEnabled) {
      newSubmitHandler()
    } else {
      oldSubmitHandler()
    }
  }

  const dropRejectionHandler = () => {
    trackEvent('Setup Guide', 'USER_UPLOAD_INCOMPATIBLE_W9_FILE')
    setFileTypeError(
      'File type not supported. Accepted file types are .png, .jpeg, .heic, .heif, and .pdf.'
    )
  }

  useEffect(() => {
    trackEvent('Setup Guide', 'VIEW_UPLOAD_DOCUMENTS')
    if (isFilesSizeLimitExceeded) {
      trackEvent('Setup Guide', 'USER_UPLOAD_LARGE_W9_FILE')
    }
  }, [trackEvent, isFilesSizeLimitExceeded])

  const uploadDocumentText = isCarrierAppNewW9UploadEnabled
    ? 'Please upload your W-9'
    : 'Please upload your W-9 and Operating Authority Certificate'

  return (
    <Box mb={2.5} mt={1.5}>
      <Box>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Typography variant='h3'>Provide W-9</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box mt={1.5}>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Typography variant='subtitle1'>{uploadDocumentText}</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box mt={1.5}>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Typography variant='body1'>You can upload them now or later.</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box mt={4}>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            {files.length > 0 && (
              <Box mb={1.5}>
                <AttachmentList
                  showIcon={false}
                  attachments={files}
                  onFileDelete={fileDeleteHandler}
                />
              </Box>
            )}
            <Box>
              <Dropzone
                dropZoneText='Upload a file or drop it here'
                onFileChange={fileChangeHandler}
                onDropRejected={dropRejectionHandler}
                acceptedFiles={VALID_CONTENT_TYPES}
                filesLimit={1}
              />
              <Box display='flex' flexDirection='column'>
                <Typography
                  variant='caption'
                  color={isFilesSizeLimitExceeded ? 'error' : 'textSecondary'}
                  style={{ marginBottom: 8 }}>
                  {caption}
                </Typography>
                {fileTypeError && (
                  <Typography variant='caption' color='error' style={{ marginBottom: 8 }}>
                    {fileTypeError}
                  </Typography>
                )}
                {errorMessages.map(errorMessage => {
                  return (
                    <Typography
                      variant='caption'
                      color='error'
                      key='error'
                      style={{ marginBottom: 8 }}>
                      {errorMessage}
                    </Typography>
                  )
                })}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {errors && (
        <Box mt={8}>
          <Grid item xs={12}>
            <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={errors} />
          </Grid>
        </Box>
      )}
      <Grid container alignItems='center' justifyContent='center'>
        <Grid item xs={12} md={8} lg={6}>
          <Box textAlign='right' mt={4}>
            <Grid container justifyContent='flex-end'>
              <Grid item xs={12} md={4}>
                <Button
                  data-test='skip-documents-button'
                  fullWidth
                  color='tertiary'
                  size='large'
                  onClick={skipHandler}>
                  Skip and Send Later
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box pt={isMobile ? 2 : 0}>
                  <Button
                    data-test='submit-documents-button'
                    fullWidth
                    size='large'
                    onClick={submitHandler}
                    disabled={
                      (isCarrierAppW9RestrictUploadEnabled && isFilesSizeLimitExceeded) ||
                      files.length < 1 ||
                      isLoading
                    }>
                    {isLoading ? 'Loading...' : 'Submit and Continue'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
